import React, { FormEvent, useCallback, useState } from 'react';
import { Button, Input, Modal } from '@cognitiv/cyprus-ui';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectJob } from 'ducks/job/selectors';
import { stopJob } from 'ducks/jobs/operators';
import { selectStopJob } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { updateSuccess } from 'ducks/settings/slices';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { StopJobPayload } from 'ducks/types/payload-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useValidate } from 'hooks/useValidation';
import { stop_job_default_state, stop_job_validation } from 'modals/job/constants';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';
import { mergeSearchQuery } from 'utils/queries';
import { v4 as uuidv4 } from 'uuid';

import cn from 'modals/Modal.module.scss';

export const StopJob = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { theme } = useSwitchTheme();

  const { job_id, job_definition_name } = useAppSelector(selectJob);
  const stop_job = useAppSelector(selectStopJob, shallowEqual);
  const [form, setForm] = useState<StopJobPayload>({ ...stop_job_default_state });
  const { errors, validate, resetFieldError } = useValidate(form, stop_job_validation);

  const closeModal = useCallback(() => {
    setForm({ ...stop_job_default_state });
    dispatch(setModal({ stop_job: false }));
  }, [dispatch]);

  const onChange = useCallback(
    (item: Partial<StopJobPayload>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const is_valid = validate();

      if (!is_valid) return;

      try {
        const queries = mergeSearchQuery(search, { update: uuidv4() });

        if (!job_id) return;

        const res: any = await stopJob(job_id, form);
        if (res?.error) return;

        closeModal();
        navigate(`${pathname}${queries}`);
        dispatch(updateSuccess(`Job has been stopped successfully`));
      } catch (err) {
        handleError(err);
      }
    },
    [closeModal, dispatch, form, job_id, navigate, pathname, search, validate],
  );

  return (
    <Modal name="stop_job" show={stop_job} width={450} padding={0} close_box={50} onClose={closeModal} standard={false}>
      <h2 className={cn.header} style={{ borderBottom: theme.border }}>
        Stop Job: {job_id} {job_definition_name}
      </h2>
      <form className={cn.body} onSubmit={onSubmit}>
        <Input
          auto_focus
          label="Message"
          value={form.message}
          onChange={(item: string) => onChange({ message: item })}
          error_message={errors.find((error) => error.key === 'message')?.error}
          error_level={getValidationError<StopJobPayload>({ errors, key: 'message' }) ? ERROR_LEVEL.ERROR : undefined}
        />
        <div className={cn.buttonContainer}>
          <Button button_size="medium" type="submit" width={115} margin="10px 0px 0px 0px">
            Stop Job
          </Button>
        </div>
      </form>
    </Modal>
  );
};
